import { useId, useRef, useState, type ReactNode } from 'react';
import { CircleCheck, Hourglass, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import { commitRequestFromProposal, type PlanProposal } from '@autoday/core';
import { useStore } from '../../store/appStore.ts';
import { blockMessage, resolvePendingPlanCommit, runPlanCommit, usePlanCommitReadiness, type PlanCommitOutcome } from '../../prep/commit.ts';
import { Badge, Button } from '../../ui/kit.tsx';
import { errorText, toast } from '../../ui/toast.tsx';
import { useToday } from '../../ui/hooks.ts';
import { randomId } from '../../platform.ts';
import { useLang } from '../../i18n/i18n.ts';
import { formatMinutes, formatWeekdayDate, localizeError } from '../../i18n/format.ts';
import { PlanTimeline } from './PlanTimeline.tsx';
import {
  approveLabel,
  approvedText,
  basisNotes,
  deadlineWhen,
  feasibilityText,
  importedText,
  planNotes,
  proposalSignature,
  proposalTimelineRows,
  sessionWhen,
  toDisplayLocal,
  type TimelineRow,
} from './prepText.ts';

export type ResolvedCommit = Awaited<ReturnType<typeof resolvePendingPlanCommit>>;
export type CommitResult = PlanCommitOutcome | { ok: false; kind: 'not-applied'; message: string };

/**
 * Ett tidigare godkännande vars svar inte kom fram. AutoDay frågar servern med samma operations-ID;
 * återanslutning godkänner aldrig något av sig själv och "inget lades in" sägs först när det är belagt.
 */
export function PendingNotice({ text, onResult }: { text?: string | null; onResult: (r: ResolvedCommit) => void }) {
  const { t } = useLang();
  const [checking, setChecking] = useState(false);
  return (
    <div className="rounded-lg border border-warn/60 bg-warn-bg p-3 text-sm">
      <p className="flex items-center gap-2 font-semibold">
        <Hourglass size={16} aria-hidden className="shrink-0" /> {t('Väntar på bekräftelse från servern', 'Waiting for confirmation from the server')}
      </p>
      {text && <p className="mt-1">{text}</p>}
      <p className="mt-1">{t('Svaret kom inte fram. AutoDay skickar inget nytt förrän det är klart hur det gick.', 'The reply did not arrive. AutoDay sends nothing new until it is clear what happened.')}</p>
      <Button
        className="mt-2"
        icon={<RefreshCw size={16} aria-hidden />}
        disabled={checking}
        onClick={async () => {
          setChecking(true);
          try {
            onResult(await resolvePendingPlanCommit());
          } catch (e) {
            onResult({ ok: false, kind: 'error', message: errorText(e) });
          } finally {
            setChecking(false);
          }
        }}
      >
        {checking ? t('Kontrollerar…', 'Checking…') : t('Kontrollera igen', 'Check again')}
      </Button>
    </div>
  );
}

/**
 * Förhandsvisning av en ny plan eller en omplanering, med ETT primärval. Förslaget skapar ingenting;
 * godkännandet kontrolleras mot aktuellt underlag (core: buildPlanCommit) och är atomiskt (prep/commit.ts).
 * Varje förslag har ett eget operations-ID: dubbelklick ger samma ID, och ett nytt förslag (andra tider,
 * eller efter ett avvisat godkännande) får alltid ett nytt – det gamla förslagets knapp godkänner aldrig nya tider.
 */
export function PlanPreview({
  proposal,
  userKey,
  extra,
  picker,
  reasons,
  secondary,
  onRefresh,
  onApproved,
  onCancel,
  onToggleInclude,
  onToggleRestore,
  onEditTemplate,
  onManual,
  onOpenEvent,
}: {
  proposal: PlanProposal;
  /** Användarens egna val (mall, ta med, återställ). Ändras förslaget medan de är oförändrade är det ett nytt förslag. */
  userKey: string;
  extra: { origin?: 'manual' | 'rule'; ruleId?: string | null; include?: string[]; restore?: string[] };
  /** Mallväljare (ny plan). */
  picker?: ReactNode;
  /** Varför en ändring föreslås (omplanering). */
  reasons?: ReactNode;
  /** Ytterligare val bredvid Avbryt (t.ex. "Inte nu"). */
  secondary?: ReactNode;
  /** Räkna om förslaget med aktuell tid och aktuellt underlag. */
  onRefresh: () => void;
  onApproved: (r: { planId: string; duplicate: boolean }) => void;
  onCancel: () => void;
  onToggleInclude?: (stepId: string) => void;
  onToggleRestore?: (stepId: string) => void;
  onEditTemplate?: () => void;
  onManual?: () => void;
  onOpenEvent?: (eventId: string) => void;
}) {
  const { state } = useStore();
  const { t } = useLang();
  const today = useToday();
  const readiness = usePlanCommitReadiness();
  const ids = useId();
  const tz = proposal.basis?.timeZone ?? state.settings.timeZone;
  const sig = proposalSignature(proposal);
  const [track, setTrack] = useState(() => ({ sig, userKey, opId: randomId('plan_'), fresh: false, changedAt: 0 }));
  if (track.sig !== sig || track.userKey !== userKey) {
    // Nytt förslag: nytt operations-ID. Ändrades det utan att användaren valt något annat markeras det.
    const fresh = track.userKey === userKey;
    setTrack({ sig, userKey, opId: randomId('plan_'), fresh, changedAt: fresh ? Date.now() : 0 });
  }
  const [withExisting, setWithExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  // Skydd mot dubbelklick innan knappen hunnit ritas om som inaktiv.
  const inFlight = useRef(false);
  const [notice, setNotice] = useState<{ kind: 'stale' | 'not-applied' | 'blocked' | 'unknown' | 'error'; text: string } | null>(null);

  const existing = proposal.kind === 'approve' ? proposal.existing : [];
  const needsChoice = existing.length > 0 && !withExisting;
  const blocked = readiness.ready ? null : (readiness.message ?? (readiness.block ? blockMessage(readiness.block) : null));
  const canApprove = proposal.status === 'ok' && readiness.ready && !busy && !needsChoice;
  const include = new Set(extra.include ?? []);
  const restore = new Set(extra.restore ?? []);

  function handle(out: CommitResult) {
    if (out.ok) {
      setNotice(null);
      toast({ kind: 'ok', text: approvedText(proposal, out.duplicate) });
      onApproved({ planId: out.planId, duplicate: out.duplicate });
      return;
    }
    if (out.kind === 'stale' || out.kind === 'not-applied') {
      // Ingenting skrevs. Räkna om med aktuell tid och nytt operations-ID; markera att förslaget är nytt.
      setNotice({ kind: out.kind, text: out.kind === 'not-applied' ? t('Inget lades in. Här är ett nytt förslag.', 'Nothing was added. Here is a new suggestion.') : out.message });
      // En plan från en annan enhet eller flik ersätter förhandsvisningen – beskedet måste överleva det.
      if (out.kind === 'stale' && 'reason' in out && (out.reason === 'plan-exists' || out.reason === 'plan-changed')) toast({ kind: 'info', text: out.message });
      setTrack((x) => ({ ...x, opId: randomId('plan_'), fresh: true }));
      onRefresh();
      return;
    }
    // Fel: ett omförsök skickas med nytt operations-ID (servern kan ha sparat svaret för det gamla).
    if (out.kind === 'error') setTrack((x) => ({ ...x, opId: randomId('plan_') }));
    setNotice({ kind: out.kind, text: out.kind === 'error' ? localizeError(out.message) : out.message });
  }

  async function approve() {
    if (!canApprove || inFlight.current) return;
    // Förslaget byttes av sig självt alldeles nyss (t.ex. en annan flik bokade en tid): godkänn inte tider som
    // användaren knappt hunnit se.
    if (track.changedAt && Date.now() - track.changedAt < 1500) {
      setNotice({ kind: 'stale', text: t('Förslaget ändrades nyss. Kontrollera tiderna och godkänn igen.', 'The suggestion just changed. Check the times and approve again.') });
      // Nästa klick är ett medvetet val av det som nu visas.
      setTrack((x) => ({ ...x, changedAt: 0 }));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    let out: CommitResult;
    try {
      const req = commitRequestFromProposal(proposal, track.opId, planNotes(proposal), { ...extra, withExisting: existing.length > 0 && withExisting });
      out = await runPlanCommit(req);
    } catch (e) {
      out = { ok: false, kind: 'error', message: errorText(e) };
    }
    inFlight.current = false;
    setBusy(false);
    handle(out);
  }

  function resolved(r: ResolvedCommit) {
    if (!r) return setNotice(null);
    const pending = readiness.pending;
    if (pending && pending.opId !== track.opId) {
      // Ett äldre godkännande (annan flik eller tidigare session). Förslaget här står kvar.
      if (r.ok) toast({ kind: 'ok', text: t('Ett tidigare godkännande är bekräftat.', 'An earlier approval has been confirmed.') });
      else if (r.kind === 'not-applied') toast({ kind: 'info', text: t('Ett tidigare godkännande lades inte in. Inget ändrades.', 'An earlier approval was not applied. Nothing changed.') });
      else if (r.kind !== 'unknown') toast({ kind: 'warn', text: r.message });
      onRefresh();
      return;
    }
    handle(r);
  }

  const rows = proposal.basis ? proposalTimelineRows(proposal, tz) : [];
  const imported = importedText(proposal.sourceImportedAt, today, tz);
  const meta = [
    proposal.basis ? `${t('Deadline', 'Deadline')} ${deadlineWhen(proposal.basis, tz)}` : null,
    proposal.sourceName ? `${proposal.sourceName}${imported ? ` (${imported})` : ''}` : imported,
    !picker && proposal.snapshot ? `${t('Mall', 'Template')}: ${proposal.snapshot.name}` : null,
    proposal.basis ? t(`Tider i ${tz}`, `Times in ${tz}`) : null,
  ].filter(Boolean);
  const reasonId = `${ids}-reason`;
  // Samma besked som skälet vid knappen visas bara en gång.
  const showNotice = notice && notice.kind !== 'unknown' && !(notice.kind === 'blocked' && notice.text === blocked);

  const rowActions = (r: TimelineRow) => {
    const s = proposal.sessions.find((x) => x.stepId === r.stepId);
    if (!s || proposal.kind !== 'replan') return null;
    const out: ReactNode[] = [];
    if (onToggleInclude && (s.action === 'manual' || include.has(s.stepId)))
      out.push(
        <label key="inc" className="flex min-h-11 items-center gap-2 text-sm">
          <input type="checkbox" className="h-5 w-5 shrink-0" checked={include.has(s.stepId)} onChange={() => onToggleInclude(s.stepId)} />
          <span>
            {t('Ta med i omplaneringen', 'Include in the replanning')}
            <span className="sr-only">: {r.title}</span>
          </span>
        </label>,
      );
    if (onToggleRestore && (s.action === 'removed' || restore.has(s.stepId)))
      out.push(
        <Button key="res" className="min-h-10 px-3" aria-pressed={restore.has(s.stepId)} onClick={() => onToggleRestore(s.stepId)}>
          {restore.has(s.stepId) ? t('Låt steget vara borttaget', 'Leave the step removed') : t('Återställ steget', 'Restore the step')}
          <span className="sr-only">: {r.title}</span>
        </Button>,
      );
    if (onOpenEvent && s.action === 'impossible' && s.eventId && s.from)
      out.push(
        <Button key="open" variant="ghost" className="min-h-10 px-3" onClick={() => onOpenEvent(s.eventId!)}>
          {t('Flytta passet själv', 'Move the session yourself')}
          <span className="sr-only">: {r.title}</span>
        </Button>,
      );
    return out.length ? out : null;
  };

  return (
    <div className="grid min-w-0 gap-3" role="group" aria-labelledby={`${ids}-head`}>
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-accent">
          <span id={`${ids}-head`}>{proposal.kind === 'replan' ? t('Förslag till ändring', 'Suggested change') : t('Förslag', 'Suggestion')}</span>
          {track.fresh && (
            <Badge tone="accent">
              <Sparkles size={12} aria-hidden /> {t('Nytt förslag', 'New suggestion')}
            </Badge>
          )}
        </p>
        <p className="mt-1 font-semibold [overflow-wrap:anywhere]">{proposal.sourceTitle}</p>
        {meta.length > 0 && <p className="text-xs text-muted [overflow-wrap:anywhere]">{meta.join(' · ')}</p>}
      </div>

      {picker}
      {reasons}

      <div aria-live="polite" className="grid gap-2 empty:hidden">
        {showNotice && <p className={`rounded-lg border px-3 py-2 text-sm ${notice.kind === 'error' ? 'border-risk/60 bg-risk-bg' : 'border-warn/60 bg-warn-bg'}`}>{notice.text}</p>}
        {(notice?.kind === 'unknown' || (readiness.pending && !busy)) && (
          <PendingNotice
            text={notice?.kind === 'unknown' ? notice.text : readiness.pending && readiness.pending.sourceKey !== proposal.sourceKey ? t('Det gäller ett tidigare godkännande för en annan deadline.', 'It concerns an earlier approval for another deadline.') : null}
            onResult={resolved}
          />
        )}
      </div>

      {proposal.status === 'unsupported' || !proposal.basis ? (
        <p className="text-sm">{feasibilityText(proposal)}</p>
      ) : (
        <>
          <PlanTimeline
            rows={rows}
            deadline={{ when: deadlineWhen(proposal.basis, tz), sentence: `${t('Deadline', 'Deadline')} ${deadlineWhen(proposal.basis, tz, true)}.` }}
            label={t('Stegen i tidsordning', 'The steps in time order')}
            actions={rowActions}
          />
          <p className="text-sm text-muted">{t(`Totalt ${formatMinutes(proposal.totalMinutes)}`, `Total ${formatMinutes(proposal.totalMinutes)}`)}</p>
          <p className={`flex items-start gap-2 text-sm ${proposal.status === 'impossible' ? 'font-medium text-warn' : ''}`}>
            {proposal.status === 'impossible' ? <TriangleAlert size={16} aria-hidden className="mt-0.5 shrink-0" /> : <CircleCheck size={16} aria-hidden className="mt-0.5 shrink-0 text-ok" />}
            <span>{feasibilityText(proposal)}</span>
          </p>
          {proposal.status === 'impossible' && (onEditTemplate || onManual) && (
            <div className="flex flex-wrap gap-2">
              {onEditTemplate && !picker && <Button onClick={onEditTemplate}>{t('Ändra mallen', 'Change the template')}</Button>}
              {onManual && <Button onClick={onManual}>{t('Välj tider själv', 'Choose times yourself')}</Button>}
            </div>
          )}
        </>
      )}

      {existing.length > 0 && proposal.status !== 'unsupported' && (
        <div className="rounded-lg border border-border-strong bg-panel p-3 text-sm">
          <p className="font-medium">{t('Det finns redan förberedelser för den här deadlinen:', 'There is already preparation for this deadline:')}</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {existing.map((e) => (
              <li key={e.id} className="[overflow-wrap:anywhere]">
                {e.title} – {e.allDay ? formatWeekdayDate(e.start.slice(0, 10)) : sessionWhen(toDisplayLocal(e.start, e.timeZone ?? undefined, tz), toDisplayLocal(e.end, e.timeZone ?? undefined, tz))}
                {e.done ? ` (${t('klar', 'done')})` : ''}
                {e.kind === 'ended-plan' ? ` – ${t('från en avslutad plan', 'from an ended plan')}` : ''}
              </li>
            ))}
          </ul>
          <label className="mt-2 flex min-h-11 items-start gap-2">
            <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={withExisting} onChange={(e) => setWithExisting(e.target.checked)} />
            <span>{t('Lägg till planen ändå – de befintliga ligger kvar', 'Add the plan anyway – the existing ones stay')}</span>
          </label>
        </div>
      )}

      {proposal.basis && <p className="text-xs text-muted">{basisNotes(proposal, readiness.mode).join(' ')}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {proposal.status === 'ok' && (
          <Button variant="primary" icon={<CircleCheck size={16} aria-hidden />} disabled={!canApprove} aria-describedby={blocked || needsChoice ? reasonId : undefined} onClick={() => void approve()}>
            {busy ? (proposal.kind === 'replan' ? t('Sparar…', 'Saving…') : t('Lägger in…', 'Adding…')) : approveLabel(proposal)}
          </Button>
        )}
        {secondary}
        <Button variant="ghost" onClick={onCancel}>
          {proposal.status === 'ok' ? t('Avbryt', 'Cancel') : t('Stäng', 'Close')}
        </Button>
      </div>
      {proposal.status === 'ok' && (blocked || needsChoice) && (
        <p id={reasonId} className="text-xs text-warn">
          {/* Rutan ovan förklarar redan det väntande godkännandet – här bara en kort hänvisning till den. */}
          {readiness.block === 'pending-commit' && readiness.pending && !busy
            ? t('Vänta tills godkännandet ovan är bekräftat.', 'Wait until the approval above is confirmed.')
            : (blocked ?? t('Välj först om planen ska läggas till trots befintliga förberedelser.', 'First choose whether to add the plan despite the existing preparation.'))}
        </p>
      )}
    </div>
  );
}
